/* Copyright (c) 2020-present CloudIO Inc */

import {
  DataRecord,
  MULTIPLE_SELECT_OPS,
  OPS_ICONS,
  SELECT_OPS_KEY_TYPE,
} from '@cloudio-saas/datasource-types';
import {
  Box,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  BaseProps,
  Form,
  isNotEmpty,
  SearchLookupField,
} from 'componentregistry';
import { useCurrentDataStore } from 'components/DataStoreReference/DataStoreReference';
import { HorizontalFormField } from 'components/Form/FormUtils';
import {
  allSelectOps,
  hasSelectEditor,
} from 'components/SmartSearch/Operators';
import {
  getName,
  getOperator,
  getOpOptions,
  getValue,
} from 'components/SmartSearch/SearchUtils';
import { FieldWrapper, useFieldVariant } from 'components/TextField/BaseField';
import {
  Option,
  SelectInputProps,
  VirtualizedMultiSelectInput,
  VirtualizedSelectInput,
} from 'custom/SelectInput';
import { useStringExpression } from 'expression/useExpression';
import { useFocus } from 'hooks/useFocus';
import Icon, { parseIconText } from 'icons/Icon';
import React, { memo, useEffect, useId, useState } from 'react';
import { useStoreAttributeFilter } from 'store/useAppStore';
import { LookupOption, useLookupValues } from 'store/utils/utils';
import { useTranslate } from 'utils/lang';
import WaveDots from 'utils/WaveDots';

interface SelectProps
  extends Required<SearchLookupField.SearchLookupField>,
    BaseProps<'SearchLookupField'> {}

interface Props extends SelectProps, Form.HorizontalFormProps {
  headerFilter?: boolean;
  onHeaderFilterSearch?: () => void;
}

function IOSearchLookupField(
  props: Props,
  ref: React.Ref<HTMLDivElement>,
): React.ReactElement {
  const {
    appUid,
    autoFocus,
    className,
    domProps,
    flex,
    formHorizontal,
    itemId,
    labelAlignment,
    labelWidth,
    lookup,
    platform,
    required,
    defaultOperator,
    viewAttribute,
    hideOperatorChooser,
    headerFilter,
    pageId,
    onHeaderFilterSearch,
  } = props;
  let { helperText, placeholder } = props;
  const { label } = props;
  const { t } = useTranslate();
  helperText = t(helperText);
  let labelResult =
    useStringExpression({
      exp: label,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'label',
        data: {},
      },
    }) || undefined;
  placeholder = t(placeholder);
  labelResult = t(labelResult);

  const inputRef = useFocus(itemId);

  const [lkpOptions, isBusy] = useLookupValues(platform, appUid, lookup);
  const variant = useFieldVariant(appUid);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    if (platform.isRecording() && !headerFilter) {
      platform.recordLine(
        itemId,
        `await ui.click({ testid: '${itemId}-op' });`,
      );
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [ops, setOps] = useState<SELECT_OPS_KEY_TYPE>(defaultOperator);
  const [value, setValue] = useState('');
  const [multiValue, setMultiValue] = useState<string[]>([]);
  const options = getOpOptions('Select', t);

  const store = useCurrentDataStore<DataRecord>();
  const filter = useStoreAttributeFilter<DataRecord>(store, viewAttribute);

  useEffect(() => {
    if (filter) {
      const op = getOperator(filter) as SELECT_OPS_KEY_TYPE;
      setOps(op);
      const val = getValue(filter) as string | string[];
      if (Array.isArray(val)) {
        setMultiValue(val);
        setValue('');
      } else {
        setValue(val);
        setMultiValue([]);
      }
    } else {
      // #974 Fix. Any filter other than values has to be set to default operator
      setOps(defaultOperator);
      setValue('');
      setMultiValue([]);
    }
  }, [defaultOperator, filter]);

  const hasEditor = hasSelectEditor(ops);

  function updateFilter(
    attr: string,
    op: SELECT_OPS_KEY_TYPE,
    val: string,
    multiVal: string[],
  ) {
    if (!store) return;

    let filters = store.getSmartSearchFilter();
    filters = filters.filter((f) => getName(f) !== attr);
    const _hasEditor = hasSelectEditor(op);
    let multi = false;
    if (op in MULTIPLE_SELECT_OPS) {
      multi = true;
    }
    if (!_hasEditor || (multi ? multiVal.length : isNotEmpty(val))) {
      const v: string | string[] = _hasEditor ? (multi ? multiVal : val) : '';
      const _filter = {
        [attr]: { [op]: v },
      };
      filters.push(_filter);
    } else {
      setOps(op);
      setValue('');
      setMultiValue([]);
    }

    store?.setSmartSeachFilter(filters);
  }

  const record = lkpOptions.find((r) => r.value === value);
  const { iconName, iconType } = parseIconText(record?.icon);

  let multiple = false;
  if (ops in MULTIPLE_SELECT_OPS) {
    multiple = true;
  }
  labelResult =
    ops === 'is' ? labelResult : `${labelResult} (${allSelectOps[ops]})`;

  const id = useId();
  const selectFieldProps: SelectInputProps<Option> = {
    id,
    autoFocus,
    value,
    variant,
    required,
    onChange: (changedVal: string, option: Option | null) => {
      const newValue = changedVal;
      setValue(newValue);
      updateFilter(viewAttribute, ops, newValue, []);
      if (platform.isRecording() && option) {
        platform.recordLine(
          itemId,
          `await ui.type('${itemId}', '${option.label}');`,
        );
      }
    },
    IOInputProps: {
      inputRef,
      startAdornment: (
        <InputAdornment position="start">
          {hideOperatorChooser ? undefined : (
            <IconButton
              data-testid={`${itemId}-op`}
              onClick={handleClick}
              data-tip={allSelectOps[ops] || ops}
            >
              <Icon iconName={OPS_ICONS[ops] || 'equals'} />
            </IconButton>
          )}
          {isBusy ? (
            <WaveDots />
          ) : record && iconName ? (
            <Icon
              iconName={iconName}
              iconType={iconType}
              color={record.color}
              className="icon"
            />
          ) : null}
        </InputAdornment>
      ),
    },
    valueAttribute: 'value',
    labelAttribute: 'label',
    options: lkpOptions,
    placeholder: hasEditor ? placeholder : allSelectOps[ops],
    disabled: !hasEditor,
    renderOption: (liprops, option) => {
      const row = option as LookupOption;
      const icon = parseIconText(row.icon);
      return (
        <Box
          component="li"
          sx={{ '& > .icon': { mr: 2, flexShrink: 0 } }}
          {...liprops}
        >
          <>
            {icon.iconName ? (
              <Icon
                iconName={icon.iconName}
                iconType={icon.iconType}
                color={row.color}
                className="icon"
              />
            ) : null}
            {row.label}
          </>
        </Box>
      );
    },
    sx: { width: headerFilter ? '300px' : undefined },
  };

  const control = multiple ? (
    formHorizontal ? (
      <VirtualizedMultiSelectInput
        {...selectFieldProps}
        value={multiValue}
        onChange={(changedVal: string[], option: Option[] | null) => {
          setMultiValue(changedVal);
          updateFilter(viewAttribute, ops, '', changedVal);
        }}
      />
    ) : (
      <VirtualizedMultiSelectInput
        {...selectFieldProps}
        label={headerFilter ? undefined : labelResult}
        helpText={helperText}
        value={multiValue}
        onChange={(changedVal: string[], option: Option[] | null) => {
          setMultiValue(changedVal);
          updateFilter(viewAttribute, ops, '', changedVal);
        }}
        limitTags={headerFilter ? 1 : undefined}
      />
    )
  ) : formHorizontal ? (
    <VirtualizedSelectInput {...selectFieldProps} />
  ) : (
    <VirtualizedSelectInput
      {...selectFieldProps}
      label={headerFilter ? undefined : labelResult}
      helpText={helperText}
    />
  );

  return (
    <>
      <FieldWrapper
        className={className}
        domProps={domProps}
        flex={flex}
        ref={ref}
      >
        {formHorizontal ? (
          <HorizontalFormField
            control={control}
            helperText={helperText}
            label={labelResult}
            labelAlignment={labelAlignment}
            labelWidth={labelWidth}
            required={required}
            id={id}
          />
        ) : (
          control
        )}
      </FieldWrapper>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose}>
        {options.map((option) => {
          return (
            <MenuItem
              data-testid={`op-${option.value}`}
              key={option.value}
              onClick={() => {
                const op = option.value as SELECT_OPS_KEY_TYPE;
                setOps(op);
                handleClose();
                updateFilter(viewAttribute, op, value, multiValue);
                if (hasSelectEditor(op)) {
                  setTimeout(() => {
                    inputRef.current?.focus();
                  }, 1);
                } else {
                  onHeaderFilterSearch?.();
                }
              }}
            >
              <ListItemIcon>
                <Icon iconName={OPS_ICONS[option.value] || 'equals'} />
              </ListItemIcon>
              <ListItemText>{option.label}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default memo(React.forwardRef(IOSearchLookupField));
